import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, } from 'lucide-react';
import MediaUploadComp from '../../../components/MediaUploadComp';
import { useCreateAddOnMutation, useGetAddOnsByIdQuery, useUpdateAddOnCardMutation, useUploadFileMutation } from '../../../redux/actions/Admin/ServiceAdmin';
import { useAlert } from '../../../components/customer/AlertContext';
import { ICreateAddOnRequest } from '../../../types/Request/AdminRequest';

interface AddOnsFormScreenProps {
    mode: 'add' | 'edit';
    packageId: number;
    addOnId: number | null;
    onClose: () => void;
}

export default function AddOnsFormScreen({ mode, packageId, onClose, addOnId }: AddOnsFormScreenProps) {

    const [formData, setFormData] = useState({
        packageId: packageId,
        description: '',
        addonTitle: '',
        price: '',
        image: '',
    });
    const { showConfirm } = useAlert();

    const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
    const [createAddOn, { isLoading: isCreating }] =
        useCreateAddOnMutation();
    const [updateAddOnCard, { isLoading: isUpdating }] =
        useUpdateAddOnCardMutation();

    const {
        data: addOnData,
        isLoading: isLoadingAddOn,
    } = useGetAddOnsByIdQuery(addOnId!, {
        skip: mode !== "edit" || !addOnId,
    });

    useEffect(() => {
        if (mode === "edit" && addOnData) {
            setFormData({
                packageId: addOnData.servicePackageId,
                addonTitle: addOnData.name,
                description: addOnData.description,
                price: String(addOnData.price),
                image: addOnData.imageUrl,
            });
        }
    }, [addOnData, mode]);

    if (isLoadingAddOn) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const handleImageChange = async (file: File) => {
        try {
            const response = await uploadFile({
                File: file,
                FolderName: "AddOns",
            }).unwrap();

            setFormData(prev => ({
                ...prev,
                image: response.fileUrl,
            }));


            showConfirm({
                type: "success",
                title: "Upload Successful",
                message: "File uploaded successfully.",
                confirmText: "OK",
                showCancel: false,
            });
        } catch (error) {
            showConfirm({
                type: "error",
                title: "Upload Failed",
                message: "Unable to upload file. Please try again.",
                confirmText: "OK",
                showCancel: false,
            });
        }
        console.log(file.size);
    };

    if (isUploading || isCreating || isUpdating) {
        return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
                    <Loader2 className="w-5 h-5 animate-spin text-mainBrandColor" />
                    <span className="font-semibold">
                        {isUploading
                            ? "Uploading file..."
                            : "Creating Add-On..."}
                    </span>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const payload: ICreateAddOnRequest = {
                servicePackageId: packageId,
                name: formData.addonTitle,
                description: formData.description,
                imageUrl: formData.image,
                price: Number(formData.price),
                isActive: true,
                sortOrder: 1,
            };

            if (mode === "edit") {
                await updateAddOnCard({
                    id: addOnId!,
                    name: formData.addonTitle,
                    description: formData.description,
                    imageUrl: formData.image,
                    price: Number(formData.price),
                    isActive: true,
                    sortOrder: addOnData?.sortOrder ?? 1,
                }).unwrap();

                showConfirm({
                    type: "success",
                    title: "Add-On Updated",
                    message: "Add-On updated successfully.",
                    confirmText: "OK",
                    showCancel: false,
                    onConfirm: onClose,
                });
            } else {
                await createAddOn(payload).unwrap();

                showConfirm({
                    type: "success",
                    title: "Add-On Created",
                    message: "Add-On created successfully.",
                    confirmText: "OK",
                    showCancel: false,
                    onConfirm: onClose,
                });
            }

        } catch (error) {
            showConfirm({
                type: "error",
                title: "Creation Failed",
                message: "Unable to create Add-On.",
                confirmText: "OK",
                showCancel: false,
            });

            console.log(error);
        }
    };

    return (
        <div className="min-h-screen px-4 py-6 flex items-center justify-center w-full">
            <div className="max-w-3xl w-full space-y-4 md:space-y-6">

                {/* Top Navigation Anchor */}
                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-xs md:text-sm font-bold text-gray-400 hover:text-logoHeading transition-colors cursor-pointer group disabled:opacity-50"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
                    Back to Tiers Board
                </button>

                {/* Master Form Card Boundary */}
                <div className="bg-white rounded-2xl md:rounded-3xl shadow-md border border-mediumgray overflow-hidden">

                    <div className="bg-white p-5 md:p-6 border-b border-mediumgray">
                        <h2 className="text-xl md:text-2xl font-black text-logoHeading tracking-tight">
                            {mode === 'edit' ? 'Modify Add-Ons' : 'Configure New Add-Ons'}
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-5 md:space-y-6">

                        {/* Row 1: Title & Subtitle */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Add On Title</label>
                                <input
                                    type="text" required
                                    placeholder="e.g., Deep Fur Conditioning"
                                    value={formData.addonTitle}
                                    onChange={(e) => setFormData({ ...formData, addonTitle: e.target.value })}
                                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-4 py-3 text-sm font-semibold transition focus:border-mainBrandColor outline-none focus:bg-white"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Add On Price</label>
                                <input
                                    type="text" required
                                    placeholder="e.g, 20.00"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-4 py-3 text-sm font-semibold transition focus:border-mainBrandColor outline-none focus:bg-white"
                                />
                            </div>
                        </div>

                        {/* Row 2: Description */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Description</label>
                            <textarea
                                required rows={2}
                                placeholder="Detail ADD-ON features..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full border border-mediumgray bg-lightgray rounded-xl px-4 py-3 text-sm font-semibold transition focus:border-mainBrandColor outline-none focus:bg-white resize-none"
                            />
                        </div>

                        <div className="w-32">
                            <MediaUploadComp
                                label="AddOn Image"
                                value={formData.image}
                                onChange={(imageUrl: string) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        image: imageUrl,
                                    }))
                                }
                                aspectRatio="aspect-square"
                                onFileSelect={handleImageChange}
                            />
                        </div>

                        {/* Bottom Actions Form Footer Panel Control Section Layout Toolbar */}
                        <div className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-4 border-t border-mediumgray">
                            <button
                                type="button" onClick={onClose}
                                disabled={isUploading || isCreating}
                                className="w-full sm:w-auto text-center border border-mediumgray bg-white px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider font-bold text-gray-500 hover:bg-lightgray transition cursor-pointer disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={isUploading || isCreating}
                                className="w-full sm:w-auto bg-mainBrandColor hover:bg-mediumGreen text-white px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider font-bold shadow-sm transition inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                            >
                                {mode === "edit" ? "Update Add On" : "Deploy Add On"}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}